import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { appMonthNameYear } from '@/utils/appDateFormat';
import Button from '../../../../components/ui/Button';
import Input from '../../../../components/ui/Input';
import Select from '../../../../components/ui/Select';
import { Checkbox } from '../../../../components/ui/Checkbox';
import YearMonthPicker from '../../../../components/yearMonthPicker';
import {
  endWpsPaidOutside,
  getWpsPaidOutside,
  putWpsPaidOutside,
  removeWpsPaidOutsidePeriod,
} from '../../../../api/Payroll/wps';
import {
  WpsConfirmWarning,
  WpsPaidOutsidePeriodView,
  WpsPaidOutsideReason,
  WpsPaidOutsideView,
} from '../../../../models/payroll';
import { getApiErrorMessage } from '../../../../utils/apiErrors';
import { InlineConfirm } from '../../../Payroll/WpsCenter/components/InlineConfirm';
import { useWpsConfirm } from '../../../Payroll/WpsCenter/components/useWpsConfirm';
import { useWpsLabels } from '../../../Payroll/WpsCenter/components/wpsLabels';

const REASONS: WpsPaidOutsideReason[] = ['Cash', 'Cheque', 'ForeignAccount', 'ForeignStaffPaidAbroad', 'Other'];
const NOT_EXEMPT = 'paidOutsideNotExemption';
const FOREIGN_STAFF = 'paidOutsideForeignStaffLimits';

const ymToDate = (ym: number): Date => new Date(Math.floor(ym / 100), (ym % 100) - 1, 1);
const dateToYm = (date: Date): number => date.getFullYear() * 100 + date.getMonth() + 1;
const ymLabel = (ym: number): string => appMonthNameYear(ymToDate(ym));

const reasonWarning = (reason: WpsPaidOutsideReason): WpsConfirmWarning => ({
  code: reason === 'ForeignStaffPaidAbroad' ? FOREIGN_STAFF : NOT_EXEMPT,
  count: 0,
  names: [],
  moreCount: 0,
});

const MonthField: React.FC<{
  label: string;
  ym: number;
  fromYm?: number;
  onChange: (ym: number) => void;
  testId: string;
}> = ({ label, ym, fromYm, onChange, testId }) => {
  const { i18n } = useTranslation();
  return (
    <div className="min-w-0 max-sm:w-full" data-testid={testId}>
      <span className="mb-1 block text-sm font-medium text-foreground">{label}</span>
      <YearMonthPicker
        date={ymToDate(ym)}
        onChange={date => onChange(dateToYm(date))}
        fromMonth={fromYm ? ymToDate(fromYm) : undefined}
        isRtl={i18n.dir() === 'rtl'}
        // Its selects have a minimum width that does not fit this card on a phone, where the arrow fell outside the card.
        // Tighter spacing and a fixed year width leave room for all four digits of the year and a full month name.
        className="max-sm:w-full max-sm:gap-1 max-sm:p-1 max-sm:[&>button]:p-0 max-sm:[&>div]:min-w-0 max-sm:[&>div]:flex-1 max-sm:[&>div]:gap-1 max-sm:[&_select]:min-w-0 max-sm:[&_select]:px-1 max-sm:[&_select:first-child]:flex-1 max-sm:[&_select:last-child]:w-[4.75rem] max-sm:[&_select:last-child]:flex-none"
      />
    </div>
  );
};

interface PeriodFormProps {
  userId: string;
  currentYm: number;
  period?: WpsPaidOutsidePeriodView;
  onDone: () => void;
  onCancel: () => void;
}

/** Starts a period, or edits the running one. Opens in place, under the bank rule it sits beside. */
const PeriodForm: React.FC<PeriodFormProps> = ({ userId, currentYm, period, onDone, onCancel }) => {
  const { t } = useTranslation();
  const labels = useWpsLabels();
  const [reason, setReason] = useState<WpsPaidOutsideReason | ''>(period?.reason ?? '');
  const [note, setNote] = useState(period?.note ?? '');
  const [fromYm, setFromYm] = useState(period?.fromYm ?? currentYm);
  const [hasUntil, setHasUntil] = useState(period?.untilYm != null);
  const [untilYm, setUntilYm] = useState(period?.untilYm ?? period?.fromYm ?? currentYm);
  const finished = useCallback(() => onDone(), [onDone]);
  const { warnings, error, busy, run, reset } = useWpsConfirm(finished);

  const noteNeeded = reason === 'Other';
  const rangeValid = !hasUntil || untilYm >= fromYm;
  const canSubmit = reason !== '' && rangeValid && (!noteNeeded || note.trim().length > 0);

  // The reason's warning is already on screen under the choice. Only what HR has not read yet, the months that change,
  // stops for a second press.
  const monthWarnings = warnings?.filter(w => w.code !== NOT_EXEMPT && w.code !== FOREIGN_STAFF) ?? null;
  const shown = [
    ...(reason !== '' ? [reasonWarning(reason)] : []),
    ...(monthWarnings ?? []),
  ];

  const submit = () => {
    if (reason === '') return;
    const input = {
      reason,
      // A note typed for Other and then hidden by another choice is not sent.
      note: noteNeeded ? note.trim() : undefined,
      fromYm,
      untilYm: hasUntil ? untilYm : null,
    };
    void run(async () => {
      const first = await putWpsPaidOutside(userId, { ...input, confirm: warnings !== null });
      const onlyTheReason = first.errors.length === 0 && !first.payload.done
        && first.payload.warnings.every(w => w.code === NOT_EXEMPT || w.code === FOREIGN_STAFF);
      return onlyTheReason ? putWpsPaidOutside(userId, { ...input, confirm: true }) : first;
    });
  };

  // A warning describes the values it was raised for, so changing one withdraws it.
  const changed = <T,>(set: (value: T) => void) => (value: T) => {
    set(value);
    reset();
  };

  return (
    <InlineConfirm
      warnings={shown}
      error={error ?? (rangeValid ? null : t('payroll.wps.uae.err.periodInvalid'))}
      busy={busy}
      confirmLabel={warnings
        ? t('payroll.wps.uae.paidOutside.saveAnyway')
        : period ? t('payroll.wps.uae.paidOutside.saveChanges') : t('payroll.wps.uae.paidOutside.save')}
      canConfirm={canSubmit}
      onConfirm={submit}
      onCancel={onCancel}
      testId="wps-paid-outside-form"
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Select
          label={t('payroll.wps.uae.paidOutside.reason')}
          placeholder={t('payroll.wps.uae.paidOutside.reasonPlaceholder')}
          value={reason}
          options={REASONS.map(value => ({ value, label: labels.outsideReason(value) }))}
          onChange={changed((value: WpsPaidOutsideReason) => setReason(value))}
          data-testid="wps-paid-outside-reason"
        />
        {noteNeeded && (
          <Input
            label={t('payroll.wps.uae.note')}
            value={note}
            maxLength={500}
            required
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => changed(setNote)(e.target.value)}
            data-testid="wps-paid-outside-note"
          />
        )}
      </div>
      <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
        <MonthField
          label={t('payroll.wps.uae.paidOutside.from')}
          ym={fromYm}
          onChange={changed(setFromYm)}
          testId="wps-paid-outside-from"
        />
        {hasUntil && (
          <MonthField
            label={t('payroll.wps.uae.paidOutside.until')}
            ym={untilYm}
            fromYm={fromYm}
            onChange={changed(setUntilYm)}
            testId="wps-paid-outside-until"
          />
        )}
      </div>
      <Checkbox
        label={t('payroll.wps.uae.paidOutside.untilToggle')}
        checked={hasUntil}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          // A last month before the from month would only be refused, so it starts on the from month.
          if (e.target.checked && untilYm < fromYm) setUntilYm(fromYm);
          changed(setHasUntil)(e.target.checked);
        }}
        data-testid="wps-paid-outside-until-toggle"
      />
    </InlineConfirm>
  );
};

interface PeriodActionProps {
  userId: string;
  currentYm: number;
  period: WpsPaidOutsidePeriodView;
  onDone: () => void;
  onCancel: () => void;
}

/** Ends the running period. A last month in the past names the months that go back to To file. */
const EndPeriodForm: React.FC<PeriodActionProps> = ({ userId, currentYm, period, onDone, onCancel }) => {
  const { t } = useTranslation();
  const [untilYm, setUntilYm] = useState(Math.max(period.fromYm, period.untilYm ?? currentYm));
  const finished = useCallback(() => onDone(), [onDone]);
  const { warnings, error, busy, run, reset } = useWpsConfirm(finished);

  return (
    <InlineConfirm
      warnings={warnings}
      error={error}
      busy={busy}
      confirmLabel={warnings ? t('payroll.wps.uae.paidOutside.endAnyway') : t('payroll.wps.uae.paidOutside.endConfirm')}
      onConfirm={() => void run(() => endWpsPaidOutside(userId, untilYm, warnings !== null))}
      onCancel={onCancel}
      testId="wps-paid-outside-end-form"
    >
      <MonthField
        label={t('payroll.wps.uae.paidOutside.lastMonth')}
        ym={untilYm}
        fromYm={period.fromYm}
        onChange={value => { setUntilYm(value); reset(); }}
        testId="wps-paid-outside-end-until"
      />
    </InlineConfirm>
  );
};

/** The fix for a period set by mistake. The first press asks the server which months change. */
const RemovePeriodForm: React.FC<Omit<PeriodActionProps, 'userId' | 'currentYm'>> = ({ period, onDone, onCancel }) => {
  const { t } = useTranslation();
  const finished = useCallback(() => onDone(), [onDone]);
  const { warnings, error, busy, run } = useWpsConfirm(finished);

  return (
    <InlineConfirm
      warnings={warnings}
      error={error}
      busy={busy}
      destructive
      confirmLabel={warnings ? t('payroll.wps.uae.paidOutside.removeAnyway') : t('payroll.wps.uae.paidOutside.removeConfirm')}
      onConfirm={() => void run(() => removeWpsPaidOutsidePeriod(period.id, warnings !== null))}
      onCancel={onCancel}
      testId="wps-paid-outside-remove-form"
    >
      <p className="text-sm text-muted-foreground">{t('payroll.wps.uae.paidOutside.removeEffect')}</p>
    </InlineConfirm>
  );
};

type OpenForm =
  | { kind: 'new' }
  | { kind: 'edit' | 'end' | 'remove'; id: number };

/**
 * "This person is paid outside WPS", on the employee page beside the bank rule. HR only and UAE only, which the page
 * decides before this renders, so nobody else fetches it. Saves on its own, independently of the page's edit mode.
 */
const WpsPaidOutside: React.FC<{ userId: string }> = ({ userId }) => {
  const { t } = useTranslation();
  const labels = useWpsLabels();
  const [view, setView] = useState<WpsPaidOutsideView | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [open, setOpen] = useState<OpenForm | null>(null);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const result = await getWpsPaidOutside(userId);
      if (result.errors.length > 0) setLoadError(getApiErrorMessage(result, t));
      else setView(result.payload);
    } catch (e) {
      setLoadError(getApiErrorMessage(e, t));
    }
  }, [userId, t]);

  useEffect(() => { void load(); }, [load]);

  const finished = useCallback(() => {
    setOpen(null);
    void load();
  }, [load]);
  const cancel = useCallback(() => setOpen(null), []);

  if (loadError) {
    return (
      <div className="mt-4 border-t border-border pt-4" data-testid="wps-paid-outside">
        <p className="text-sm text-red-700 dark:text-red-300" role="alert">{loadError}</p>
        <Button size="xs" variant="ghost" className="mt-1" onClick={() => void load()}>{t('Try again')}</Button>
      </div>
    );
  }
  if (!view) return null;
  // Turned off with nothing recorded: there is nothing to show and nothing HR can do here.
  if (!view.enabled && view.periods.length === 0) return null;

  const canAdd = view.enabled && !view.periods.some(p => p.isRunning && p.untilYm == null);

  return (
    <div className="mt-4 border-t border-border pt-4" data-testid="wps-paid-outside">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <h4 className="text-sm font-medium text-foreground">{t('payroll.wps.uae.paidOutside.title')}</h4>
          {view.periods.length === 0 && (
            <p className="text-sm text-muted-foreground">{t('payroll.wps.uae.paidOutside.none')}</p>
          )}
          {!view.enabled && (
            <p className="text-sm text-muted-foreground">{t('payroll.wps.uae.paidOutside.turnedOff')}</p>
          )}
        </div>
        {canAdd && open?.kind !== 'new' && (
          <Button size="sm" variant="outline" onClick={() => setOpen({ kind: 'new' })} data-testid="wps-paid-outside-open">
            {t('payroll.wps.uae.paidOutside.record')}
          </Button>
        )}
      </div>

      {open?.kind === 'new' && (
        <PeriodForm userId={userId} currentYm={view.currentYm} onDone={finished} onCancel={cancel} />
      )}

      {view.periods.length > 0 && (
        <ul className="mt-2 divide-y divide-border">
          {view.periods.map(period => {
            const opened = open && open.kind !== 'new' && open.id === period.id ? open.kind : null;
            return (
              <li key={period.id} className="py-2" data-testid="wps-paid-outside-row">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0 text-sm text-foreground">
                    <p>
                      {period.untilYm == null
                        ? t('payroll.wps.uae.paidOutside.since', { from: ymLabel(period.fromYm), reason: labels.outsideReason(period.reason) })
                        : t('payroll.wps.uae.paidOutside.range', { from: ymLabel(period.fromYm), until: ymLabel(period.untilYm), reason: labels.outsideReason(period.reason) })}
                    </p>
                    {period.note && <p className="text-muted-foreground break-words">{period.note}</p>}
                  </div>
                  {!opened && (
                    <div className="flex flex-wrap items-center gap-1">
                      {period.isRunning && view.enabled && (
                        <Button size="xs" variant="ghost" onClick={() => setOpen({ kind: 'edit', id: period.id })} data-testid="wps-paid-outside-edit">
                          {t('payroll.wps.uae.paidOutside.edit')}
                        </Button>
                      )}
                      {period.isRunning && (
                        <Button size="xs" variant="ghost" onClick={() => setOpen({ kind: 'end', id: period.id })} data-testid="wps-paid-outside-end">
                          {t('payroll.wps.uae.paidOutside.end')}
                        </Button>
                      )}
                      <Button size="xs" variant="ghost" onClick={() => setOpen({ kind: 'remove', id: period.id })} data-testid="wps-paid-outside-remove">
                        {t('payroll.wps.uae.paidOutside.remove')}
                      </Button>
                    </div>
                  )}
                </div>
                {opened === 'edit' && (
                  <PeriodForm userId={userId} currentYm={view.currentYm} period={period} onDone={finished} onCancel={cancel} />
                )}
                {opened === 'end' && (
                  <EndPeriodForm userId={userId} currentYm={view.currentYm} period={period} onDone={finished} onCancel={cancel} />
                )}
                {opened === 'remove' && <RemovePeriodForm period={period} onDone={finished} onCancel={cancel} />}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default WpsPaidOutside;
